import React from "react";
import { Layout } from "../Layout";
import { PageHeader } from "../components/PageHeader";
import { CvChart, CvEntry, CvTrack } from "../components/CvChart";

// Newest first within each track.
const cvEntries: CvEntry[] = [
    {
        id: "software-developer",
        title: "Software developer",
        short: "Software developer",
        tiny: "Dev",
        place: "TIROLER VERSICHERUNG V.a.G., Innsbruck, Austria",
        start: 2024,
        end: null,
        track: "work",
        details:
            "I am part of a team of seven developers building internal applications for insurance sales and maintaining the customer portal.\n\n" +
            "For an extended period I was the sole developer of the letter system (Briefsystem), which staff use to create customer letters and send them by post or email. " +
            "Now I work on full-stack web applications, mainly in Java and partly in Angular/TypeScript.",
    },
    {
        id: "civil-service",
        title: "Civil service",
        short: "Civil service",
        place: "Jugendland, Innsbruck, Austria",
        start: 2022,
        end: 2023,
        track: "work",
        details:
            "I completed my Austrian civil service at Jugendland, a private childcare organization in Innsbruck that also operates residential groups for children from challenging family backgrounds. The kindergarten includes an inclusive group with children requiring special attention, such as those on the autism spectrum.\n\n" +
            "My role involved assisting educators in daily activities, supervising children during play and learning, and providing one-on-one support for one of the two autistic children, ensuring their safety, engagement, and integration within the group. " +
            "This experience strengthened my patience, empathy, communication skills, and ability to adapt to diverse needs in a team-oriented environment.",
    },
    {
        id: "university",
        title: "BSc Computer Science",
        short: "University",
        tiny: "Uni",
        place: "University of Innsbruck, Austria",
        start: 2023,
        end: 2026,
        track: "education",
        details:
            "I studied the 6-semester Bachelor's programme in Computer Science, conducted mainly in German, which teaches core principles of programming, algorithms & data structures, databases, operating systems, networks, and software engineering. The curriculum combines theoretical foundations with practical coursework and project work.",
    },
    {
        id: "htl",
        title: "Higher Technical Institute (HTL), Industrial engineering and business informatics",
        short: "HTL",
        place: "HTL Anichstraße, Innsbruck, Austria",
        start: 2017,
        end: 2022,
        track: "education",
        details:
            "I completed a five-year technical program in business informatics, engineering, and software development, " +
            "strengthening my skills in programming, databases, and project management.\n\n" +
            "During summer breaks, I completed four internships: two at Swarovski (Wattens), " +
            "where I supported maintenance work alongside an electronics technician, and two at multi24 GmbH, " +
            "providing IT support and troubleshooting for various clients in Tyrol.\n\n" +
            "For my diploma thesis, two classmates and I developed a Flutter-based mobile app for invoice digitization, using image preprocessing, edge detection with OpenCV, and OCR with Tesseract to extract relevant invoice data. Through this project, I gained hands-on experience in cross-platform mobile development, backend integration, and designing practical document-processing workflows.",
    },
    {
        id: "high-school",
        title: "High school",
        short: "High school",
        tiny: "High",
        place: "Reithmann-Gymnasium, Innsbruck, Austria",
        start: 2013,
        end: 2017,
        track: "education",
        details:
            "During this period of my life I developed a strong interest in computer science and programming in general by exploring practical applications of code. I taught myself Java and object-oriented programming concepts through creating plugins and mods for video games.",
    },
    {
        id: "primary-school",
        title: "Primary school",
        short: "Primary school",
        tiny: "Primary",
        place: "Volksschule Reichenau, Innsbruck, Austria",
        start: 2009,
        end: 2013,
        track: "education",
        details:
            "I developed an early passion for computing by tackling simple \"drag-and-drop\" coding games and helping my family troubleshoot basic PC issues. This sparked my curiosity in how technology works and set the foundation for my later studies in computer science.",
    },
];

// PDFs live directly in /public: SpaController forwards any path whose first
// segment has no dot (e.g. /cv/file.pdf) to React, so they must not be in a folder.
const CV_PDFS = [
    { href: "/Manuel-Repetschnig-CV-EN.pdf", lang: "en", label: "CV in English (PDF)" },
    { href: "/Manuel-Repetschnig-Lebenslauf-DE.pdf", lang: "de", label: "Lebenslauf auf Deutsch (PDF)" },
];

const SECTIONS: { track: CvTrack; title: string; ring: string; key: string }[] = [
    { track: "work",      title: "Work",      ring: "border-gentian", key: "bg-gentian" },
    { track: "education", title: "Education", ring: "border-loden",   key: "bg-loden" },
];

const duration = (e: CvEntry) => {
    if (e.end === null) return "ongoing";
    const years = e.end - e.start;
    return `${years} ${years === 1 ? "year" : "years"}`;
};

function Entry({ entry, ring, last }: { entry: CvEntry; ring: string; last: boolean }) {
    const ongoing = entry.end === null;
    return (
        <article id={`cv-${entry.id}`} className={`relative pl-8 scroll-mt-24 ${last ? "" : "pb-10"}`}>
            {/* trail segment + waypoint ring (filled while ongoing) */}
            <span
                className={`absolute left-[5px] w-0 border-l-2 border-dashed border-contour top-0 ${last ? "h-3" : "bottom-0"}`}
                aria-hidden
            />
            <span
                className={`absolute left-0 top-[0.4rem] w-3 h-3 rounded-full border-2 ${ring} ${ongoing ? ring.replace("border-", "bg-") : "bg-paper"}`}
                aria-hidden
            />

            <p className="m-0 font-label text-base font-semibold text-ink">
                {entry.start} – {ongoing ? "today" : entry.end}
                <span className="label font-medium ml-2">{duration(entry)}</span>
            </p>
            <h3 className="text-[1.375rem] mt-1 mb-0">{entry.title}</h3>
            <p className="m-0 mt-0.5 mb-2 text-ink">{entry.place}</p>
            {entry.details.split("\n\n").map((para, i) => (
                <p key={i} className="m-0 mb-3 last:mb-0 text-muted">{para}</p>
            ))}
        </article>
    );
}

const CVPage: React.FC = () => {
    const today = new Date();
    const now = today.getFullYear() + today.getMonth() / 12;

    return (
        <Layout>
            <PageHeader title="CV" lead="Work and education since 2009.">
                <div className="flex flex-wrap gap-3">
                    {CV_PDFS.map(pdf => (
                        <a
                            key={pdf.lang}
                            href={pdf.href}
                            hrefLang={pdf.lang}
                            type="application/pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary"
                        >
                            {pdf.label}
                        </a>
                    ))}
                </div>
            </PageHeader>

            <div className="page-container pt-8 pb-14">
                <CvChart entries={cvEntries} now={now} />
            </div>

            <div className="page-container pb-24 grid gap-14 md:grid-cols-2 md:gap-12 lg:gap-16">
                {SECTIONS.map(section => {
                    const entries = cvEntries.filter(e => e.track === section.track);
                    return (
                        <section key={section.track} aria-labelledby={`cv-${section.track}`}>
                            <h2 id={`cv-${section.track}`} className="text-[1.75rem] mb-6 flex items-center gap-3">
                                <span className={`inline-block w-6 h-2.5 rounded-sm ${section.key}`} aria-hidden />
                                {section.title}
                            </h2>
                            {entries.map((entry, i) => (
                                <Entry key={entry.id} entry={entry} ring={section.ring} last={i === entries.length - 1} />
                            ))}
                        </section>
                    );
                })}
            </div>
        </Layout>
    );
};

export default CVPage;
